<template>
  <div class="px-5 sm:px-6 lg:px-10">
    <!-- Header -->
    <div class="flex items-center justify-between mt-12">
      <h1 class="text-[22px] sm:text-[24px] leading-7 font-bold text-gray-900">
        {{ isEdit ? 'Edit Payment' : 'Add Payment' }}
      </h1>

      <template v-if="isEdit">
        <!-- bank_transfer = true → Exit button -->
        <button
          v-if="originalBankTransfer"
          type="button"
          class="inline-flex items-center gap-2 h-9 px-4 rounded-full text-sm font-bold text-white shadow-sm hover:opacity-95"
          style="background-color:red"
          @click="$router.back()"
        >
          Exit
        </button>

        <!-- bank_transfer = false → Save button -->
        <button
          v-else
          type="button"
          class="inline-flex items-center gap-2 h-9 px-4 rounded-full text-sm font-bold text-white shadow-sm hover:opacity-95"
          style="background-color:#5B9AC5"
          @click="handleSubmit"
        >
          Save
        </button>
      </template>
    </div>

    <!-- Top row -->
    <div class="mt-4 mb-12 flex items-end justify-between">
      <div v-if="!isEdit" class="w-auto">
        <label class="block text-sm font-bold text-gray-700 mb-1" for="payment_id">Payment ID</label>
        <input
          id="payment_id"
          name="payment_id"
          v-model="model.payment_id"
          type="text"
          placeholder="0000"
          class="w-56 h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none"
        />
      </div>

      <div v-else class="flex-1 flex items-end justify-between gap-6">
        <div class="w-auto">
          <label class="block text-sm font-bold text-gray-700 mb-1" for="customer_name">Customer Name</label>
          <input
            id="customer_name"
            name="customer_name"
            v-model="model.customer_name"
            type="text"
            placeholder="[customer name]"
            readonly
            class="w-96 h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none bg-gray-50 cursor-default"
          />
        </div>
        <label class="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="model.bank_transfer"
            :true-value="true"
            :false-value="false"
            :disabled="model.bank_transfer === true"
            class="df-checkbox"
          />
          <span>Bank Transfer</span>
        </label>
      </div>

      <!-- Right: Add button only in Add mode -->
      <button
        v-if="!isEdit"
        type="button"
        class="inline-flex items-center gap-2 h-9 px-4 rounded-full text-sm font-bold text-white shadow-sm hover:opacity-95"
        style="background-color:#5B9AC5"
        @click="handleSubmit"
      >
        Add payment
      </button>
    </div>

    <!-- Card with checkboxes + form -->
    <div class="mb-4 bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <!-- Checkboxes header -->
      <div class="px-5 mt-16 flex justify-center">
        <div class="flex flex-wrap items-center gap-12">
          <label class="inline-flex items-center gap-2" :class="isEdit ? 'cursor-default opacity-60' : 'cursor-pointer'">
            <input type="checkbox" class="df-checkbox" :checked="selectedType==='contracts'" @change="!isEdit && selectType('contracts')" :disabled="isEdit" />
            <span>Contracts</span>
          </label>
          <label class="inline-flex items-center gap-2" :class="isEdit ? 'cursor-default opacity-60' : 'cursor-pointer'">
            <input type="checkbox" class="df-checkbox" :checked="selectedType==='booking'" @change="!isEdit && selectType('booking')" :disabled="isEdit" />
            <span>Booking</span>
          </label>
          <label class="inline-flex items-center gap-2" :class="isEdit ? 'cursor-default opacity-60' : 'cursor-pointer'">
            <input type="checkbox" class="df-checkbox" :checked="selectedType==='companies'" @change="!isEdit && selectType('companies')" :disabled="isEdit" />
            <span>Companies</span>
          </label>
          <label class="inline-flex items-center gap-2" :class="isEdit ? 'cursor-default opacity-60' : 'cursor-pointer'">
            <input type="checkbox" class="df-checkbox" :checked="selectedType==='lawsuit'" @change="!isEdit && selectType('lawsuit')" :disabled="isEdit" />
            <span>Lawsuits</span>
          </label>
          <label class="inline-flex items-center gap-2" :class="isEdit ? 'cursor-default opacity-60' : 'cursor-pointer'">
            <input type="checkbox" class="df-checkbox" :checked="selectedType==='sold_car'" @change="!isEdit && selectType('sold_car')" :disabled="isEdit" />
            <span>Sold car</span>
          </label>
        </div>
      </div>

      <div class="p-5 sm:p-8">
        <DynamicForm
          :title="''"
          :schema="formSchema"
          v-model="model"
          :mode="isEdit ? 'view' : 'add'"
          :showActions="false"
          @submit="handleSubmit"
        >
          <template #field:__section_title_payment_details>
            <h3 class="text-lg font-bold text-gray-900">Payment Details</h3>
          </template>

          <!-- ── Check inline row: Check Bank / Check No / Due Date ── -->
          <template #field:__check_inline_row>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-bold text-gray-700 mb-1" for="check_bank">Check Bank</label>
                <input
                  id="check_bank"
                  name="check_bank"
                  v-model="model.check_bank"
                  type="text"
                  placeholder="Enter check bank"
                  :readonly="isEdit"
                  :class="isEdit ? 'bg-gray-50 cursor-default' : ''"
                  class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none focus:ring-0 focus:border-gray-300"
                />
              </div>
              <div>
                <label class="block text-sm font-bold text-gray-700 mb-1" for="check_number">Check No</label>
                <input
                  id="check_number"
                  name="check_number"
                  v-model="model.check_number"
                  type="text"
                  placeholder="Enter check number"
                  :readonly="isEdit"
                  :class="isEdit ? 'bg-gray-50 cursor-default' : ''"
                  class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none focus:ring-0 focus:border-gray-300"
                />
              </div>
              <div>
                <label class="block text-sm font-bold text-gray-700 mb-1" for="due_date">Due Date</label>
                <input
                  id="due_date"
                  name="due_date"
                  v-model="model.due_date"
                  type="date"
                  :readonly="isEdit"
                  :class="isEdit ? 'bg-gray-50 cursor-default' : ''"
                  class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none focus:ring-0 focus:border-gray-300"
                />
              </div>
            </div>
          </template>

          <template #field:bank_name_input>
            <div>
              <input
                id="bank_name_input"
                name="bank_name_input"
                v-model="model.bank_name"
                type="text"
                placeholder="Enter bank Name"
                :readonly="isEdit"
                :class="isEdit ? 'bg-gray-50 cursor-default' : ''"
                class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none focus:ring-0 focus:border-gray-300"
              />
            </div>
          </template>

          <template #field:contract_value_input>
            <div>
              <input
                id="contract_value_input"
                name="contract_value_input"
                v-model="model.payment_value"
                type="text"
                placeholder="135"
                :readonly="isEdit"
                :class="isEdit ? 'bg-gray-50 cursor-default' : ''"
                class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none focus:ring-0 focus:border-gray-300"
              />
            </div>
          </template>
        </DynamicForm>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import DynamicForm from '@/components/DynamicForm.vue'

export default {
  name: 'PaymentAddEdit',
  components: { DynamicForm },

  data() {
    return {
      isEdit: false,
      paymentId: null,
      paymentSource: null,
      selectedType: null,
      originalBankTransfer: false,
      model: {
        payment_id:          '',
        customer_name:       '',
        bank_transfer:       false,
        date:                '',
        payment_method:      '',
        payment_value:       '',
        total_payment_value: '',
        bank_name:           '',
        auth_number:         '',
        credit_card_number:  '',
        check_bank:          '',
        check_number:        '',
        due_date:            '',
      },

      paymentMethodOptionsAdd: [
        { value: 'cash',          label: 'Cash' },
        { value: 'credit_card',   label: 'Credit Card' },
        { value: 'check',         label: 'Check' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'smart_link',    label: 'Smart Link' },
        { value: 'online',        label: 'Online' },
      ],
      paymentMethodOptionsEdit: [
        { value: 'cash',          label: 'Cash' },
        { value: 'credit_card',   label: 'Credit Card' },
        { value: 'check',         label: 'Check' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'smart_link',    label: 'Smart Link' },
        { value: 'online',        label: 'Online' },
      ],
    }
  },

  computed: {
    paymentMethodOptions() {
      return this.isEdit ? this.paymentMethodOptionsEdit : this.paymentMethodOptionsAdd
    },

    paymentExtraFields() {
      const pm = (this.model.payment_method || '').toLowerCase()

      const bankNameField = {
        key:     'bank_name_input',
        label:   'Bank Name',
        type:    'slot',
        colSpan: 2,
        props:   this.isEdit ? { disabled: true } : {}
      }

      if (!pm) return []
      if (pm === 'cash')          return [bankNameField]
      if (pm === 'bank_transfer') return [bankNameField]
      if (pm === 'smart_link')    return [bankNameField]
      if (pm === 'online')        return [bankNameField]
      if (pm === 'credit_card') {
        return [
          { key: 'auth_number',        label: 'AUTH Number',        type: 'text', placeholder: 'Enter auth number',        props: this.isEdit ? { readonly: true } : {} },
          { key: 'credit_card_number', label: 'Credit Card Number', type: 'text', placeholder: 'Enter credit card number', props: this.isEdit ? { readonly: true } : {} },
          bankNameField,
        ]
      }
      if (pm === 'check') {
        return [
          { key: '__check_inline_row', type: 'slot', colSpan: 2 },
          bankNameField,
        ]
      }
      return []
    },

    paymentBasicsBlock() {
      const ro  = this.isEdit ? { readonly: true } : {}
      const dis = this.isEdit ? { disabled: true  } : {}
      return [
        { key: '__section_title_payment_details', label: '', type: 'slot', colSpan: 2 },
        { key: 'payment_value',       label: 'Payment Value',       type: 'number', placeholder: 'Enter Payment Value',          props: ro },
        { key: 'payment_method',      label: 'Payment Method',      type: 'select', options: this.paymentMethodOptions,
          placeholder: 'Please select payment method', props: dis },
        ...this.paymentExtraFields,
        { key: 'total_payment_value', label: 'Total Payment Value', type: 'number', placeholder: 'Enter payment value', colSpan: 2, props: ro },
      ]
    },

    /* ── ADD field sets ── */
    add_fields_contracts() {
      return [
        { key: 'contract_id',          label: 'Contract ID',   type: 'text', placeholder: '0000' },
        { key: 'date',                 label: 'Date',          type: 'date' },
        { key: 'customer_name',        label: 'Customer Name', type: 'text', placeholder: '[customer name]', colSpan: 2 },
        { key: 'contract_value_input', label: 'Contract Value / Violation Value / Extra Money', type: 'slot', colSpan: 2 },
        ...this.paymentBasicsBlock,
      ]
    },
    add_fields_booking() {
      return [
        { key: 'booking_id',           label: 'Booking ID',    type: 'text', placeholder: '0000' },
        { key: 'date',                 label: 'Date',          type: 'date' },
        { key: 'customer_name',        label: 'Customer Name', type: 'text', placeholder: '[customer name]', colSpan: 2 },
        { key: 'contract_value_input', label: 'Contract Value / Violation Value / Extra Money', type: 'slot', colSpan: 2 },
        ...this.paymentBasicsBlock,
      ]
    },
    add_fields_companies() {
      return [
        { key: 'date',                 label: 'Date',         type: 'date',   colSpan: 2 },
        { key: 'company_id',           label: 'Company Name', type: 'select', options: this.companyOptions(), placeholder: '', colSpan: 2 },
        { key: 'contract_value_input', label: 'Contract Value / Violation Value / Extra Money', type: 'slot', colSpan: 2 },
        ...this.paymentBasicsBlock,
      ]
    },
    add_fields_lawsuit() {
      return [
        { key: 'lawsuit_id',   label: 'Lawsuite ID',   type: 'text',   placeholder: 'Enter lawsuit ID' },
        { key: 'company_id',   label: 'Company',       type: 'select', options: this.companyOptions(), placeholder: '' },
        { key: 'lawsuit_type', label: 'Lawsuite Type', type: 'text',   placeholder: 'lawsuit type' },
        { key: 'court',        label: 'Court',         type: 'text',   placeholder: 'Enter court' },
        { key: 'lawyer',       label: 'Lawyer',        type: 'text',   placeholder: 'lawyer' },
        { key: 'lawsuit_date', label: 'Lawsuit Date',  type: 'date' },
        { key: 'party',        label: 'Party',         type: 'text',   placeholder: 'Party' },
        { key: 'feez',         label: 'Feez',          type: 'text',   placeholder: 'feez' },
        { key: 'amount',       label: 'Amount',        type: 'number', placeholder: 'amount' },
        { key: 'date',         label: 'Date',          type: 'date' },
        ...this.paymentBasicsBlock,
      ]
    },
    add_fields_soldcar() {
      return [
        { key: 'sold_car_id',      label: 'Sold Car ID',     type: 'text',   placeholder: 'Enter sold car ID' },
        { key: 'vehicle',          label: 'Vehicle',         type: 'text',   placeholder: 'Vehicle' },
        { key: 'customer_name',    label: 'Customer Name',   type: 'text',   placeholder: '[customer name]', colSpan: 2 },
        { key: 'date',             label: 'Date',            type: 'date' },
        { key: 'remaining_amount', label: 'Remaining Amount',type: 'number', placeholder: '33' },
        ...this.paymentBasicsBlock,
      ]
    },

    /* ── EDIT field sets ── */
    edit_fields_contracts() {
      const ro = { readonly: true }
      return [
        { key: 'contract_id',          label: 'Contract ID',   type: 'text', placeholder: '0000',          props: ro },
        { key: 'date',                 label: 'Date',          type: 'date', props: ro },
        { key: 'customer_name',        label: 'Customer Name', type: 'text', placeholder: 'customer name',  props: ro, colSpan: 2 },
        { key: 'contract_value_input', label: 'Contract Value / Violation Value / Extra Money', type: 'slot', colSpan: 2 },
        ...this.paymentBasicsBlock,
      ]
    },
    edit_fields_booking() {
      const ro = { readonly: true }
      return [
        { key: 'booking_id',           label: 'Booking ID',    type: 'text', placeholder: '0000',                props: ro },
        { key: 'date',                 label: 'Date',          type: 'date', props: ro },
        { key: 'customer_name',        label: 'Customer Name', type: 'text', placeholder: 'Enter customer name', props: ro, colSpan: 2 },
        { key: 'contract_value_input', label: 'Contract Value / Violation Value / Extra Money', type: 'slot', colSpan: 2 },
        ...this.paymentBasicsBlock,
      ]
    },
    edit_fields_companies() {
      const ro = { readonly: true }
      return [
        { key: 'date',          label: 'Date',          type: 'date', props: ro },
        { key: 'customer_name', label: 'Customer Name', type: 'text', placeholder: 'Enter Customer Name', props: ro, colSpan: 2 },
        ...this.paymentBasicsBlock,
      ]
    },
    edit_fields_lawsuit() {
      const ro  = { readonly: true }
      const dis = { disabled: true }
      return [
        { key: 'lawsuit',      label: 'Lawsuite',      type: 'select', options: this.lawsuitOptions(),  placeholder: 'Select lawsuit', props: dis },
        { key: 'company_id',   label: 'Company',       type: 'select', options: this.companyOptions(),  placeholder: '',               props: dis },
        { key: 'lawsuit_type', label: 'Lawsuite Type', type: 'text',   placeholder: 'lawsuit type',     props: ro },
        { key: 'court',        label: 'Court',         type: 'text',   placeholder: 'Enter court',      props: ro },
        { key: 'lawyer',       label: 'Lawyer',        type: 'text',   placeholder: 'lawyer',           props: ro },
        { key: 'lawsuit_date', label: 'Lawsuit Date',  type: 'date',   props: ro },
        { key: 'party',        label: 'Party',         type: 'text',   placeholder: 'Party',            props: ro },
        { key: 'feez',         label: 'Feez',          type: 'text',   placeholder: 'feez',             props: ro },
        { key: 'amount',       label: 'Amount',        type: 'number', placeholder: 'amount',           props: ro },
        { key: 'date',         label: 'Date',          type: 'date',   props: ro },
        ...this.paymentBasicsBlock,
      ]
    },
    edit_fields_soldcar() {
      const ro = { readonly: true }
      return [
        { key: 'sold_car_id',      label: 'Sold Car ID',      type: 'text',   placeholder: 'Enter sold car ID', props: ro },
        { key: 'vehicle',          label: 'Vehicle',          type: 'text',   placeholder: 'Vehicle',           props: ro },
        { key: 'customer_name',    label: 'Customer Name',    type: 'text',   placeholder: '[customer name]',   props: ro, colSpan: 2 },
        { key: 'date',             label: 'Date',             type: 'date',   props: ro },
        { key: 'remaining_amount', label: 'Remaining Amount', type: 'number', placeholder: '33',                props: ro },
        ...this.paymentBasicsBlock,
      ]
    },

    formSchema() {
      const mapAdd = {
        contracts: this.add_fields_contracts,
        booking:   this.add_fields_booking,
        companies: this.add_fields_companies,
        lawsuit:   this.add_fields_lawsuit,
        sold_car:  this.add_fields_soldcar,
      }
      const mapEdit = {
        contracts: this.edit_fields_contracts,
        booking:   this.edit_fields_booking,
        companies: this.edit_fields_companies,
        lawsuit:   this.edit_fields_lawsuit,
        sold_car:  this.edit_fields_soldcar,
      }
      const fields = (this.isEdit ? mapEdit : mapAdd)[this.selectedType] || []
      return { sections: [{ title: '', cols: 2, fields }] }
    },
  },

  watch: {
    'model.payment_method'() {
      if (!this.isEdit) this.clearPaymentExtras()
    },

    'model.payment_value'(newVal) {
      this.model.total_payment_value = newVal
    },
  },

  created() {
    const state = window.history.state || {}
    const mode  = state.paymentMode ?? this.$route.query.mode
    const type  = state.paymentType ?? this.$route.query.type
    const id    = state.paymentId   ?? this.$route.query.id

    this.isEdit    = mode === 'edit'
    this.paymentId = id ?? null
    this.selectedType = this.isEdit ? (type || 'contracts') : null

    if (this.isEdit && this.paymentId) {
      this.fetchExisting()
    } else {
      this.model = { ...this.model, date: this.today(), payment_method: '' }
    }
  },

  methods: {
    today() {
      return new Date().toISOString().slice(0, 10)
    },

    selectType(t) {
      this.selectedType = t
      this.model.payment_method = ''
      this.clearPaymentExtras()
    },

    clearPaymentExtras() {
      const extraKeys = ['auth_number', 'credit_card_number', 'check_bank', 'check_number', 'due_date']
      extraKeys.forEach(k => { this.model[k] = '' })
    },

    companyOptions() { return [] },
    lawsuitOptions() { return [] },

    async fetchExisting() {
      try {
        const res = await api.get(`/accounting/payments/${this.paymentId}`)
        const p = res.data?.payment ?? res.data

        let paymentDetails = {}
        try {
          if (p.payment_details) {
            const parsedOnce = JSON.parse(p.payment_details)
            paymentDetails = typeof parsedOnce === 'string'
              ? JSON.parse(parsedOnce)
              : parsedOnce
          }
        } catch (e) {
          console.warn('payment_details parse failed', e)
        }

        this.paymentSource = p.source ?? null
        if (p.payment_for) {
          // Map API payment_for format to internal format
          const paymentForMap = {
            'Sold Car': 'sold_car',
            'Contracts': 'contracts',
            'Booking': 'booking',
            'Companies': 'companies',
            'Lawsuits': 'lawsuit',
            'sold_car': 'sold_car',
            'contracts': 'contracts',
            'booking': 'booking',
            'companies': 'companies',
            'lawsuit': 'lawsuit',
          }
          this.selectedType = paymentForMap[p.payment_for] || p.payment_for
        }

        this.model = {
          payment_id:          p.id ?? this.paymentId,
          customer_name:       p.customer_name ?? '',
          bank_transfer:       !!p.transferred_to_bank,
          date:                p.date ?? this.today(),
          payment_method:      p.payment_method ?? '',
          payment_value:       p.payment_value ?? '',
          total_payment_value: p.total_payment_value ?? p.payment_value ?? '',
          contract_id:         p.contract_ref ?? '',
          bank_name:           p.bank_name ?? '',
          auth_number:         paymentDetails.auth_number         ?? '',
          credit_card_number:  paymentDetails.credit_card_number  ?? '',
          // check fields — mapped from payment_details keys
          check_bank:          paymentDetails.check_bank_name     ?? '',
          check_number:        paymentDetails.check_number        ?? '',
          due_date:            paymentDetails.check_due_date      ?? '',
        }

        this.originalBankTransfer = !!p.transferred_to_bank

      } catch (e) {
        console.error('failed to load payment', e)
      }
    },

    async handleSubmit() {
      if (this.isEdit) {
        if (this.model.bank_transfer === this.originalBankTransfer) {
          this.$router.push({ name: 'Payments' })
          return
        }

        try {
          await api.post('/accounting/payments/transfer-to-bank', {
            id:     this.paymentId,
            source: this.paymentSource,
          })
          this.$router.push({ name: 'Payments' })
        } catch (e) {
          console.error('transfer-to-bank failed', e)
        }
        return
      }

      // Add mode
      try {
        const payload = this.preparePayload()
        await api.post('/accounting/payments/addNew', payload)
        this.$router.push({ name: 'Payments' })
      } catch (e) {
        console.error('submit failed', e)
      }
    },

    preparePayload() {
      const typeMap = {
        contracts: 'booking_id',
        booking:   'booking_id',
        companies: 'customer_id',
        lawsuit:   'lawsuits_id',
        sold_car:  'sold_car_id',
      }

      const formData = new FormData()
      formData.append('payment_for', this.selectedType)

      Object.entries(this.model || {}).forEach(([k, v]) => {
        if (v !== null && v !== undefined) formData.append(k, v)
      })

      if (this.model.payment_for_id && typeMap[this.selectedType]) {
        formData.append(typeMap[this.selectedType], this.model.payment_for_id)
      }

      return formData
    },
  },
}
</script>

<style scoped>
:deep(td) { @apply text-base; }

:deep(input[type="checkbox"].df-checkbox) {
  appearance: none; -webkit-appearance: none;
  width: 18px; height: 18px;
  border: 1.8px solid #000 !important;
  border-radius: 3px;
  background: #fff !important;
  cursor: pointer; position: relative;
  vertical-align: middle;
  --tw-ring-color: transparent;
  --tw-ring-shadow: 0 0 #0000;
  --tw-ring-offset-shadow: 0 0 #0000;
  accent-color: initial;
}
:deep(input[type="checkbox"].df-checkbox:checked),
:deep(input[type="checkbox"].df-checkbox:checked:hover),
:deep(input[type="checkbox"].df-checkbox:checked:focus) {
  background: #fff !important;
  border-color: #000 !important;
}
:deep(input[type="checkbox"].df-checkbox:checked)::after {
  content: "";
  position: absolute;
  left: 4px; top: 0px;
  width: 6px; height: 10px;
  border-right: 2px solid #000;
  border-bottom: 2px solid #000;
  transform: rotate(45deg);
}
:deep(input[type="checkbox"].df-checkbox:focus),
:deep(input[type="checkbox"].df-checkbox:focus-visible) {
  outline: none; box-shadow: none;
}

:deep(input[readonly]),
:deep(input:disabled),
:deep(select:disabled) {
  background-color: #f9fafb !important;
  cursor: default !important;
  color: #374151;
}

:deep(input:focus),
:deep(select:focus),
:deep(textarea:focus) {
  outline: none !important;
  box-shadow: none !important;
  --tw-ring-offset-shadow: none !important;
  --tw-ring-shadow: none !important;
  border-color: #d1d5db !important;
}
</style>
